import React, { useState } from "react";
import {
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  FormControl,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link as MuiLink,
  Box,
} from "@mui/material";
import {
  VisibilityIcon as Visibility,
  VisibilityOffIcon as VisibilityOff,
} from "../../icons";
import { observer } from "mobx-react";
import { useRootStore } from "../../store/contexts";
import { Link, useNavigate } from "react-router";
import { usePasswordValidation, PasswordRequirementsList } from "../";
import { API_URL } from "../../api/apiClient";

type ResetPasswordFormProps = {
  token: string | null;
};

export const ResetPasswordForm = observer(
  ({ token }: ResetPasswordFormProps) => {
    const rootStore = useRootStore();
    const lang = rootStore.langStore;
    const navigate = useNavigate();

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    const paperStyle = {
      padding: 20,
      height: "auto",
      minHeight: "450px",
      width: 320,
      margin: "20px auto",
    };
    const logoStyle = {
      maxWidth: "80%",
      height: "auto",
      objectFit: "contain" as const,
      marginTop: 8,
      marginBottom: 8,
    };

    const validation = usePasswordValidation(newPassword, confirmPassword);

    const handleSubmit = async () => {
      if (!token) {
        setError(lang.invalidOrExpiredToken || "Invalid or expired link");
        return;
      }
      if (!validation.isValid) {
        setError(
          lang.passwordRequirementsNotMet ||
            "Password does not meet requirements"
        );
        return;
      }

      setLoading(true);
      setError("");

      try {
        const response = await fetch(`${API_URL}/api/auth/reset-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, newPassword }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          setSuccess(true);
          setTimeout(() => navigate("/login"), 3000);
        } else {
          if (data.error === "invalid_or_expired_token") {
            setError(lang.invalidOrExpiredToken || "Invalid or expired link");
          } else if (data.error === "weak_password") {
            setError(
              lang.passwordRequirementsNotMet ||
                "Password does not meet requirements"
            );
          } else {
            setError(lang.serverError || "Server error");
          }
        }
      } catch (err) {
        setError(lang.serverError || "Server error");
      } finally {
        setLoading(false);
      }
    };

    if (!token) {
      return (
        <Grid>
          <Paper elevation={10} style={paperStyle}>
            <Grid container direction="column" alignItems="center">
              <img src="/logo1.png" alt="Logo" style={logoStyle} />
              <h2>{lang.resetPasswordTitle}</h2>
            </Grid>
            <Typography
              color="error"
              style={{ textAlign: "center", marginTop: 20 }}
            >
              {lang.invalidOrExpiredToken || "Invalid or expired link"}
            </Typography>
            <MuiLink
              component={Link}
              to="/forgot-password"
              style={{ display: "block", marginTop: 20, textAlign: "center" }}
            >
              {lang.requestNewLinkLabel || "Request new link"}
            </MuiLink>
          </Paper>
        </Grid>
      );
    }

    if (success) {
      return (
        <Grid>
          <Paper elevation={10} style={paperStyle}>
            <Grid container direction="column" alignItems="center">
              <img src="/logo1.png" alt="Logo" style={logoStyle} />
              <h2>{lang.passwordChangedTitle}</h2>
            </Grid>
            <Typography style={{ marginTop: 20, textAlign: "center" }}>
              {lang.passwordChangedMessage}
            </Typography>
            <MuiLink
              component={Link}
              to="/login"
              style={{ display: "block", marginTop: 20, textAlign: "center" }}
            >
              {lang.logInLabel}
            </MuiLink>
          </Paper>
        </Grid>
      );
    }

    return (
      <Grid>
        <Paper elevation={10} style={paperStyle}>
          <Grid container direction="column" alignItems="center">
            <img src="/logo1.png" alt="Logo" style={logoStyle} />
            <h2>{lang.resetPasswordTitle}</h2>
          </Grid>
          <FormControl fullWidth>
            <TextField
              name="new-password"
              id="new-password"
              sx={{ m: 1 }}
              label={lang.newPasswordLabel}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              fullWidth
              required
              disabled={loading}
            />
            <TextField
              name="confirm-password"
              id="confirm-password"
              sx={{ m: 1 }}
              label={lang.confirmPasswordLabel}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm password"
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              fullWidth
              required
              disabled={loading}
              error={confirmPassword.length > 0 && !validation.passwordsMatch}
            />

            <Box sx={{ mx: 1, mt: 1 }}>
              <PasswordRequirementsList
                validation={validation}
                labels={{
                  minLength: lang.passwordMinLength,
                  uppercase: lang.passwordRequiresUppercase,
                  lowercase: lang.passwordRequiresLowercase,
                  number: lang.passwordRequiresNumber,
                  special: lang.passwordRequiresSpecial,
                  notCommon: lang.passwordNotCommon,
                  match: lang.passwordsMatchLabel,
                }}
              />
            </Box>

            <Button
              type="submit"
              color="primary"
              variant="contained"
              sx={{ m: 1 }}
              fullWidth
              onClick={handleSubmit}
              disabled={loading || !validation.isValid}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                lang.resetPasswordLabel
              )}
            </Button>
            {error && (
              <Typography color="error" sx={{ m: 1, textAlign: "center" }}>
                {error}
              </Typography>
            )}
          </FormControl>
        </Paper>
      </Grid>
    );
  }
);
